import React from 'react';

/**
 * FiltrosProduto — grupo CANÔNICO de toggles de disponibilidade de produto
 * (handoff design_handoff_catalogo). Fonte única da verdade: a lista viva é o
 * próprio PRODUTO_FILTROS abaixo — não repita os rótulos aqui, o cabeçalho
 * enumerado envelhece a cada filtro novo e passa a mentir.
 * Antes cada tela (CatalogoDigital, GerarCatalogo) definia um `Toggle` local e
 * repetia os rótulos à mão — gerando divergência. Centralizar aqui mata o drift:
 * renomeie/adicione um filtro UMA vez em PRODUTO_FILTROS e toda tela acompanha.
 *
 * Estilo Íris: o toggle usa os tokens do design system (track `bg-accent` ligado,
 * `bg-surface-2` desligado) em vez do `bg-blue-600` legado — consistente com o
 * resto do app. Controlado: `value` (objeto de booleans) + `onChange` (recebe um
 * patch `{ [key]: bool }`, padrão `onChange={patch => setConfig({ ...config, ...patch })}`).
 */

export type ProdutoFiltroKey =
  | 'apenasAtivo'
  | 'apenasComEstoque'
  | 'apenasDisponivelVenda'
  | 'apenasEnviaMobile'
  | 'exibirEmTransito'
  | 'excluirSemAtacado'
  | 'exigirMinAtacado';

export interface ProdutoFiltroDef {
  key: ProdutoFiltroKey;
  label: string;
  hint: string;
}

/** Registro canônico: todo filtro de produto com seu rótulo + explicação. */
export const PRODUTO_FILTROS: ProdutoFiltroDef[] = [
  { key: 'apenasAtivo',           label: 'Apenas ativos',                hint: 'Esconde produtos inativados no ERP.' },
  { key: 'apenasComEstoque',      label: 'Apenas com estoque',           hint: 'Só itens com saldo físico disponível.' },
  { key: 'apenasDisponivelVenda', label: 'Apenas disponível para venda', hint: 'Respeita o flag de venda do ERP.' },
  { key: 'apenasEnviaMobile',     label: 'Apenas para Mobile',           hint: 'Produtos marcados para enviar ao app.' },
  { key: 'exibirEmTransito',      label: 'Exibir produtos em trânsito',  hint: 'Inclui saldo de NFe em viagem (selo "Em viagem").' },
  { key: 'excluirSemAtacado',     label: 'Excluir produtos sem atacado', hint: 'Esconde itens que a loja marcou no ERP como fora da venda por atacado.' },
  { key: 'exigirMinAtacado',      label: 'Exigir mínimo de atacado no carrinho', hint: 'Vale por variação (embalagem): o cliente não consegue pedir menos que a quantidade mínima cadastrada no ERP para aquele código.' },
];

/** Conjunto padrão = os quatro filtros comuns às duas telas de catálogo, em ordem. */
export const FILTROS_PADRAO: ProdutoFiltroKey[] = [
  'apenasAtivo', 'apenasComEstoque', 'apenasDisponivelVenda', 'apenasEnviaMobile',
];

/**
 * Toggle individual no estilo Íris (track accent/surface-2). Exportado para os
 * toggles que NÃO são filtro de produto (ex.: "Exibir WhatsApp") usarem o mesmo
 * visual sem recriar o componente.
 */
export const FiltroToggle: React.FC<{ label: string; checked: boolean; onChange: (v: boolean) => void }> = ({ label, checked, onChange }) => (
  <label className="flex cursor-pointer select-none items-center gap-2.5">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? 'bg-accent' : 'border border-line bg-surface-2'}`}
    >
      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
    <span className="text-sm text-tx-2">{label}</span>
  </label>
);

export interface FiltrosProdutoProps {
  /** Estado atual, keyado por filtro. Aceita o objeto de config inteiro (chaves extras são ignoradas). */
  value: Partial<Record<ProdutoFiltroKey, boolean>>;
  /** Recebe um patch `{ [key]: bool }` quando um toggle muda. Mescle no estado do pai. */
  onChange: (patch: Partial<Record<ProdutoFiltroKey, boolean>>) => void;
  /** Quais filtros renderizar, em ordem. Default: FILTROS_PADRAO. */
  filters?: ProdutoFiltroKey[];
  /** Toggles extras específicos da tela (ex.: "Exibir WhatsApp"), na MESMA linha flex. */
  children?: React.ReactNode;
  className?: string;
}

/** Renderiza o conjunto canônico no wrapper `flex flex-wrap gap-x-6 gap-y-3`. */
export const FiltrosProduto: React.FC<FiltrosProdutoProps> = ({
  value, onChange, filters = FILTROS_PADRAO, children, className = '',
}) => {
  const defs = filters
    .map((k) => PRODUTO_FILTROS.find((f) => f.key === k))
    .filter((f): f is ProdutoFiltroDef => Boolean(f));

  return (
    <div className={`flex flex-wrap gap-x-6 gap-y-3 ${className}`}>
      {defs.map((f) => (
        <FiltroToggle
          key={f.key}
          label={f.label}
          checked={!!value[f.key]}
          onChange={(v) => onChange({ [f.key]: v })}
        />
      ))}
      {children}
    </div>
  );
};
